import { useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { useObjectUrlSrc } from "./useObjectUrlSrc"

/**
 * Video uploader widget. Parity: `creatives.js:203-258`.
 *
 *   - Hidden `<input type="file">` triggered by a custom button.
 *   - Selected files render as a grid of `<video controls>` previews
 *     with order badges and per-item trash buttons.
 *   - Blob URLs are created on mount of each preview and revoked on
 *     unmount / reorder (ref callback cleanup, see `useObjectUrlSrc`).
 *   - Reset the hidden input's `value` after add so re-selecting the
 *     same file works.
 */

interface Props {
  files: File[]
  onAdd: (files: File[], covers: (File | null)[]) => void
  onRemove: (index: number) => void
}

const COVER_VERTICAL = { width: 720, height: 1280 }
const COVER_SQUARE = { width: 640, height: 640 }
const VIDEO_EXTENSIONS = /\.(mp4|mov|m4v|webm|mkv|avi|mpeg|mpg)$/i

function waitForMediaEvent(
  media: HTMLVideoElement,
  event: "loadedmetadata" | "loadeddata" | "seeked" | "canplay",
): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(
      () => finish(new Error(`Timed out while reading ${media.currentSrc || "video"}`)),
      15_000,
    )
    const finish = (error?: Error) => {
      window.clearTimeout(timeout)
      media.removeEventListener(event, onReady)
      media.removeEventListener("error", onError)
      if (error) reject(error)
      else resolve()
    }
    const onReady = () => finish()
    const onError = () =>
      finish(new Error(media.error?.message || "The browser could not decode this video"))
    media.addEventListener(event, onReady, { once: true })
    media.addEventListener("error", onError, { once: true })
  })
}

function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error("Could not create a video cover")),
      "image/jpeg",
      0.92,
    )
  })
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function isLikelyVideo(file: File): boolean {
  return file.type.startsWith("video/") || VIDEO_EXTENSIONS.test(file.name)
}

/** Wait until the browser exposes decodeable frame dimensions (MOV/HEVC often lag). */
async function waitForVideoDimensions(video: HTMLVideoElement): Promise<void> {
  if (video.videoWidth > 0 && video.videoHeight > 0) return

  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
    try {
      await waitForMediaEvent(video, "loadeddata")
    } catch {
      // Continue — some browsers never fire loadeddata for QuickTime.
    }
  }

  if (Number.isFinite(video.duration) && video.duration > 0.05) {
    try {
      const seeked = waitForMediaEvent(video, "seeked")
      video.currentTime = Math.min(0.1, Math.max(0, video.duration - 0.05))
      await seeked
    } catch {
      // Ignore seek failures; dimensions may still appear after a short wait.
    }
  }

  for (let attempt = 0; attempt < 10; attempt += 1) {
    if (video.videoWidth > 0 && video.videoHeight > 0) return
    await sleep(100)
  }

  // Last resort: briefly play muted to force a decoded frame (Safari/Chrome + MOV).
  try {
    await video.play()
    await sleep(150)
    video.pause()
  } catch {
    // Autoplay / codec restrictions are fine — caller will decide.
  }

  if (!(video.videoWidth > 0 && video.videoHeight > 0)) {
    throw new Error("video dimensions are unavailable")
  }
}

/** Decode a representative frame and normalize it to a TikTok-supported size. */
async function createVideoCover(file: File): Promise<File> {
  if (!isLikelyVideo(file)) {
    throw new Error(`${file.name}: select a video file`)
  }

  const url = URL.createObjectURL(file)
  const video = document.createElement("video")
  video.preload = "auto"
  video.muted = true
  video.playsInline = true
  video.crossOrigin = "anonymous"

  try {
    const metadataReady = waitForMediaEvent(video, "loadedmetadata")
    video.src = url
    video.load()
    await metadataReady
    await waitForVideoDimensions(video)

    if (Number.isFinite(video.duration) && video.duration > 0.2) {
      try {
        const seeked = waitForMediaEvent(video, "seeked")
        video.currentTime = Math.min(video.duration * 0.1, video.duration - 0.05)
        await seeked
      } catch {
        // Keep the first decoded frame if seeking fails.
      }
    }

    const ratio = video.videoWidth / video.videoHeight
    const target =
      Math.abs(ratio - 1) <= Math.abs(ratio - (9 / 16))
        ? COVER_SQUARE
        : COVER_VERTICAL
    const targetRatio = target.width / target.height
    let sourceWidth = video.videoWidth
    let sourceHeight = video.videoHeight
    let sourceX = 0
    let sourceY = 0

    if (ratio > targetRatio) {
      sourceWidth = Math.round(sourceHeight * targetRatio)
      sourceX = Math.round((video.videoWidth - sourceWidth) / 2)
    } else {
      sourceHeight = Math.round(sourceWidth / targetRatio)
      sourceY = Math.round((video.videoHeight - sourceHeight) / 2)
    }

    const canvas = document.createElement("canvas")
    canvas.width = target.width
    canvas.height = target.height
    const context = canvas.getContext("2d")
    if (!context) throw new Error(`${file.name}: canvas is unavailable`)
    context.drawImage(
      video,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      0,
      0,
      target.width,
      target.height,
    )
    const blob = await canvasToJpeg(canvas)
    const base = file.name.replace(/\.[^.]+$/, "") || "video"
    return new File([blob], `${base}_cover.jpg`, { type: "image/jpeg" })
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    throw new Error(message.startsWith(`${file.name}:`) ? message : `${file.name}: ${message}`, {
      cause: error,
    })
  } finally {
    video.removeAttribute("src")
    video.load()
    URL.revokeObjectURL(url)
  }
}

function VideoCard({
  file,
  index,
  onRemove,
}: {
  file: File
  index: number
  onRemove: () => void
}) {
  const srcRef = useObjectUrlSrc(file)
  const [ratio, setRatio] = useState<number>(9 / 16)
  const [aspectLabel, setAspectLabel] = useState<string>("")

  return (
    <div className="relative w-[170px] overflow-hidden rounded-md border border-[#3a3a3a] bg-black">
      <div className="w-full bg-black" style={{ aspectRatio: `${ratio}` }}>
        <video
          ref={srcRef}
          controls
          muted
          playsInline
          preload="metadata"
          className="block h-full w-full object-contain"
          onLoadedMetadata={(e) => {
            const w = e.currentTarget.videoWidth || 0
            const h = e.currentTarget.videoHeight || 1
            if (!w || !h) return
            setRatio(w / h)
            setAspectLabel(prettyAspect(w, h))
          }}
        />
      </div>
      <span className="absolute left-1.5 top-1.5 rounded bg-black/70 px-1.5 py-0.5 text-[10px]">
        #{index + 1}
      </span>
      {aspectLabel ? (
        <span className="absolute left-10 top-1.5 rounded bg-[#7a8aef]/85 px-1.5 py-0.5 text-[10px] font-semibold text-white">
          {aspectLabel}
        </span>
      ) : null}
      <button
        type="button"
        className="absolute right-1.5 top-1.5 rounded bg-red-900/80 px-1.5 py-0.5 text-[10px] text-white hover:bg-red-800"
        onClick={onRemove}
        aria-label={`Remove video ${index + 1}`}
      >
        🗑
      </button>
      <div className="truncate bg-[#242424] px-2 py-1 text-[11px] text-white">
        {file.name}
      </div>
    </div>
  )
}

/** Detect well-known aspect buckets; fall back to exact "W:H". */
function prettyAspect(w: number, h: number): string {
  if (!w || !h) return ""
  const ratio = w / h
  const buckets: { label: string; r: number }[] = [
    { label: "9:16", r: 9 / 16 },
    { label: "1:1", r: 1 },
    { label: "4:5", r: 4 / 5 },
    { label: "16:9", r: 16 / 9 },
    { label: "4:3", r: 4 / 3 },
  ]
  for (const b of buckets) {
    if (Math.abs(ratio - b.r) / b.r < 0.03) return b.label
  }
  const g = gcd(w, h)
  return `${Math.round(w / g)}:${Math.round(h / g)}`
}

function gcd(a: number, b: number): number {
  return b ? gcd(b, a % b) : a
}



export function VideoUploader({ files, onAdd, onRemove }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [preparing, setPreparing] = useState(false)
  const [preparationErrors, setPreparationErrors] = useState<string[]>([])

  const prepareFiles = async (selected: File[]) => {
    setPreparing(true)
    setPreparationErrors([])
    try {
      const accepted: { file: File; cover: File | null }[] = []
      const warnings: string[] = []
      for (const file of selected) {
        if (!isLikelyVideo(file)) {
          warnings.push(`${file.name}: select a video file`)
          continue
        }
        try {
          accepted.push({ file, cover: await createVideoCover(file) })
        } catch (error) {
          // Browser often cannot decode iPhone .MOV/HEVC for canvas; still
          // accept the video and let the backend generate the cover.
          accepted.push({ file, cover: null })
          const reason =
            error instanceof Error ? error.message : String(error)
          warnings.push(
            `${file.name}: browser cover skipped (${reason.replace(`${file.name}: `, "")}); server will generate it`,
          )
        }
      }
      if (accepted.length > 0) {
        onAdd(
          accepted.map((item) => item.file),
          accepted.map((item) => item.cover),
        )
      }
      setPreparationErrors(warnings)
    } finally {
      setPreparing(false)
    }
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        multiple
        className="hidden"
        onChange={(e) => {
          const list = Array.from(e.target.files ?? [])
          // Allow re-selecting the same file.
          e.target.value = ""
          if (list.length > 0) void prepareFiles(list)
        }}
      />
      <Button
        type="button"
        variant="secondary"
        onClick={() => inputRef.current?.click()}
        disabled={preparing}
        className="border border-[#8fa0ef]/40 bg-[#7488e8] text-white hover:bg-[#6b7edc]"
      >
        {preparing ? "Preparing video covers…" : "Add videos"}
      </Button>
      {preparationErrors.length > 0 ? (
        <div className="mt-2 rounded border border-amber-500/70 bg-amber-950/30 p-2 text-xs text-amber-200">
          <p className="font-semibold">Cover notes:</p>
          <ul className="mt-1 list-disc pl-4">
            {preparationErrors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {files.length > 0 ? (
        <>
          <div className="mt-2 text-sm text-white">
            {files.length} video{files.length === 1 ? "" : "s"} selected
          </div>
          <div className="mt-2 flex flex-wrap items-start gap-3">
            {files.map((file, index) => (
              <VideoCard
                key={`${file.name}-${index}-${file.size}`}
                file={file}
                index={index}
                onRemove={() => onRemove(index)}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  )
}
